import { BASE_URL } from "../utils/requestUtils";
import {
  GetLabsBookingError,
  type GetLabsBookingOutcome,
} from "./GetLabsBookingError";

export {
  GetLabsBookingError,
  type GetLabsBookingOutcome,
} from "./GetLabsBookingError";

export interface GetLabsBookRequest {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
  dateOfBirth: string;
  estimatedDueDate?: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  postalCode: string;
  bookingKey: string;
  hasKit: boolean;
  schedulingNotes?: string;
  contactConsent: boolean;
  /** Idempotency key: a replay returns the original booking, never a second one. */
  bookingId: string;
  portalAccountPublicId?: string;
  trackingId?: string;
}

export interface GetLabsBookResponse {
  bookingId: string;
  appointmentId: string;
  identifier?: string | null;
  startAt?: string | null;
  endAt?: string | null;
}

/** Booking write surface. Read-only availability calls live separately. */
export class GetLabsApiService {
  private static readonly BOOK_PATH = "/api/v1/getlabs_bookings";

  static async bookAppointment(
    input: GetLabsBookRequest,
  ): Promise<GetLabsBookResponse> {
    const response = await fetch(`${BASE_URL}${this.BOOK_PATH}/`, {
      method: "POST",
      signal: AbortSignal.timeout(20_000),
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        first_name: input.firstName,
        last_name: input.lastName,
        phone_number: input.phoneNumber,
        email: input.email,
        date_of_birth: input.dateOfBirth,
        estimated_due_date: input.estimatedDueDate || null,
        address_line_1: input.addressLine1,
        address_line_2: input.addressLine2 || "",
        city: input.city,
        state: input.state,
        postal_code: input.postalCode,
        booking_key: input.bookingKey,
        has_kit: input.hasKit,
        scheduling_notes: input.schedulingNotes || "",
        contact_consent: input.contactConsent,
        request_id: input.bookingId,
        ...(input.portalAccountPublicId
          ? { portal_account_public_id: input.portalAccountPublicId }
          : {}),
        ...(input.trackingId ? { tracking_id: input.trackingId } : {}),
      }),
    });
    if (!response.ok) {
      // DRF rejects invalid input before the booking service makes a vendor
      // write, so a 400 is safe to send back to the editable fallback flow.
      let bookingOutcome: GetLabsBookingOutcome = "unknown";
      if (response.status === 400) bookingOutcome = "not_created";
      if (response.status === 409) bookingOutcome = "conflict";
      try {
        const errorBody: { booking_outcome?: unknown } = await response.json();
        if (
          errorBody.booking_outcome === "rejected" ||
          errorBody.booking_outcome === "unknown" ||
          errorBody.booking_outcome === "not_created"
        ) {
          bookingOutcome = errorBody.booking_outcome;
        }
      } catch {
        // A non-JSON error cannot prove that appointment creation failed.
      }
      throw new GetLabsBookingError(bookingOutcome, response.status);
    }
    if (response.status === 202) {
      // The vendor create may have succeeded. Wait for reconciliation before
      // telling the patient the appointment is booked.
      throw new GetLabsBookingError("unknown", response.status);
    }
    const data: {
      booking_id: string;
      appointment_id: string;
      identifier?: string | null;
      status?: string | null;
      start_at?: string | null;
      end_at?: string | null;
    } = await response.json();
    if (
      !data.appointment_id ||
      ["cancelled", "canceled", "completed"].includes(
        data.status?.toLowerCase() ?? "",
      )
    ) {
      throw new GetLabsBookingError("unknown", response.status);
    }
    return {
      bookingId: data.booking_id,
      appointmentId: data.appointment_id,
      identifier: data.identifier,
      startAt: data.start_at,
      endAt: data.end_at,
    };
  }
}
